import { useEffect, useRef, useState } from 'react'
import {
  Compass,
  RefreshCw,
  Brain,
  Glasses,
  BarChart3,
  ShieldCheck,
  ChevronRight,
  X,
} from 'lucide-react'

interface ExpertiseArea {
  id: string
  icon: React.ReactNode
  title: string
  shortDesc: string
  fullDesc: string
  features: string[]
  outcomes: string[]
}

const expertiseAreas: ExpertiseArea[] = [
  {
    id: 'strategic-consulting',
    icon: <Compass size={32} aria-hidden="true" />,
    title: 'Strategic EdTech Consulting',
    shortDesc: 'Develop comprehensive, future-ready technology strategies.',
    fullDesc:
      'We help institutions develop comprehensive, future-ready technology strategies. Our expert consulting ensures your EdTech investments align with institutional goals, maximize ROI, and create lasting impact on learner outcomes.',
    features: [
      'Technology roadmapping & planning',
      'Vendor evaluation & selection',
      'Implementation strategy design',
      'Stakeholder alignment workshops',
    ],
    outcomes: [
      'Clear 3-5 year EdTech roadmap',
      'Vendor shortlist with scoring matrix',
      'Implementation timeline & budget',
    ],
  },
  {
    id: 'digital-transformation',
    icon: <RefreshCw size={32} aria-hidden="true" />,
    title: 'Digital Transformation',
    shortDesc: 'Navigate modernization with confidence and clarity.',
    fullDesc:
      'Navigate the complexities of modernizing your institution with confidence. We guide you through seamless technology integration, ensuring your organization embraces change while maintaining operational excellence.',
    features: [
      'Change management frameworks',
      'Process optimization & automation',
      'Staff enablement & training',
      'Legacy system modernization',
    ],
    outcomes: [
      'Reduced operational friction',
      'Higher adoption rates',
      'Measurable efficiency gains',
    ],
  },
  {
    id: 'ai-learning',
    icon: <Brain size={32} aria-hidden="true" />,
    title: 'AI-Enhanced Learning Design',
    shortDesc: 'Create adaptive, personalized learning at scale.',
    fullDesc:
      'Harness the power of artificial intelligence to create adaptive, personalized learning experiences. We design AI-powered solutions that respond to individual learner needs, improving engagement and outcomes at scale.',
    features: [
      'Adaptive learning pathways',
      'Intelligent tutoring systems',
      'Predictive analytics integration',
      'AI-ready content architecture',
    ],
    outcomes: [
      'Personalized learner journeys',
      'Early intervention insights',
      'Improved completion rates',
    ],
  },
  {
    id: 'immersive-solutions',
    icon: <Glasses size={32} aria-hidden="true" />,
    title: 'Immersive Learning Solutions',
    shortDesc: 'Engage learners through VR, AR, and gamification.',
    fullDesc:
      'Transform education with cutting-edge immersive technologies. We design VR, AR, and gamified experiences that engage learners in ways traditional methods cannot, creating memorable and effective learning moments.',
    features: [
      'Virtual reality (VR) experiences',
      'Augmented reality (AR) overlays',
      'Gamification & game-based learning',
      'Simulation & scenario design',
    ],
    outcomes: [
      'Higher engagement scores',
      'Better knowledge retention',
      'Safe practice environments',
    ],
  },
  {
    id: 'data-analytics',
    icon: <BarChart3 size={32} aria-hidden="true" />,
    title: 'Data Analysis & Reporting',
    shortDesc: 'Turn learning data into actionable insights.',
    fullDesc:
      'Turn raw educational data into actionable insights that drive informed decision-making. Our analytics solutions help you understand learner behavior, measure effectiveness, and continuously improve your programs.',
    features: [
      'Learning analytics dashboards',
      'Custom reporting solutions',
      'ROI measurement frameworks',
      'Data-driven optimization',
    ],
    outcomes: [
      'Real-time visibility into learner progress',
      'Evidence-based program improvements',
      'Clear ROI documentation',
    ],
  },
  {
    id: 'accessibility',
    icon: <ShieldCheck size={32} aria-hidden="true" />,
    title: 'Accessibility & Compliance',
    shortDesc: 'Ensure inclusive learning that reaches everyone.',
    fullDesc:
      'Ensure your digital learning resources are inclusive and reach every learner. We help you achieve and maintain WCAG 2.1 AA compliance while creating genuinely accessible experiences that go beyond checkbox compliance.',
    features: [
      'WCAG 2.1 AA compliance audits',
      'Accessibility remediation services',
      'Inclusive design consulting',
      'ADA compliance strategies',
    ],
    outcomes: [
      'Full regulatory compliance',
      'Broader learner reach',
      'Reduced legal risk',
    ],
  },
]

export const Expertise: React.FC = () => {
  const [expandedCard, setExpandedCard] = useState<string | null>(null)
  const [visibleCards, setVisibleCards] = useState<Set<string>>(new Set())
  const sectionRef = useRef<HTMLElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-id')
            if (id) {
              setVisibleCards((prev) => new Set([...prev, id]))
            }
          }
        })
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px',
      }
    )

    const cards = sectionRef.current?.querySelectorAll('[data-id]')
    cards?.forEach((card) => observer.observe(card))

    return () => observer.disconnect()
  }, [])

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setExpandedCard(null)
      }
    }

    if (expandedCard) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
      modalRef.current?.focus()
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [expandedCard])

  const expandedArea = expertiseAreas.find((a) => a.id === expandedCard)

  return (
    <section
      id="expertise"
      ref={sectionRef}
      className="section-alt scroll-mt-20 overflow-hidden"
      aria-labelledby="expertise-heading"
    >
      {/* Decorative background elements */}
      <div className="absolute inset-0 bg-dots-pattern opacity-30" aria-hidden="true" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-blue/5 rounded-full blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-amber/5 rounded-full blur-3xl" aria-hidden="true" />

      <div className="container-wide relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 bg-brand-blue/10 text-brand-blue text-sm font-semibold rounded-full mb-4">
            Our Services
          </span>
          <h2
            id="expertise-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6"
          >
            What We Do
          </h2>
          <p className="text-lg text-slate-600">
            Expert EdTech solutions that transform how organizations approach learning.
            Click any card to explore how we can help you.
          </p>
        </div>

        {/* Expertise Grid */}
        <div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          role="list"
          aria-label="Our expertise areas"
        >
          {expertiseAreas.map((area, index) => (
            <article
              key={area.id}
              data-id={area.id}
              className={`card group cursor-pointer transition-all duration-700 ${
                visibleCards.has(area.id)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
              style={{
                transitionDelay: `${index * 100}ms`,
              }}
              role="listitem"
              onClick={() => setExpandedCard(area.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setExpandedCard(area.id)
                }
              }}
              tabIndex={0}
              aria-label={`${area.title}. Click to learn more.`}
            >
              {/* Icon */}
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-blue/10 to-brand-amber/10 flex items-center justify-center text-brand-blue mb-5 group-hover:scale-110 group-hover:from-brand-blue group-hover:to-brand-blue-dark group-hover:text-white group-hover:shadow-lg group-hover:shadow-brand-blue/30 transition-all duration-300">
                {area.icon}
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-brand-blue transition-colors">
                {area.title}
              </h3>
              <p className="text-slate-600 mb-4">{area.shortDesc}</p>

              {/* Learn More Indicator */}
              <div className="flex items-center gap-1 text-sm font-medium text-brand-blue opacity-0 group-hover:opacity-100 transition-opacity">
                Learn more
                <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Expanded Modal */}
      {expandedCard && expandedArea && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
          onClick={() => setExpandedCard(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div
            ref={modalRef}
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
            tabIndex={-1}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-slate-100 p-6 flex items-start justify-between rounded-t-3xl">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-blue to-brand-amber flex items-center justify-center text-white">
                  {expandedArea.icon}
                </div>
                <h3 id="modal-title" className="text-2xl font-bold text-slate-900">
                  {expandedArea.title}
                </h3>
              </div>
              <button
                onClick={() => setExpandedCard(null)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
                aria-label="Close details"
              >
                <X size={24} aria-hidden="true" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              <p className="text-lg text-slate-600 leading-relaxed">
                {expandedArea.fullDesc}
              </p>

              {/* Features */}
              <div>
                <h4 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-3">
                  What We Deliver
                </h4>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {expandedArea.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-2 text-slate-700"
                    >
                      <span className="w-2 h-2 rounded-full bg-brand-blue mt-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Outcomes */}
              <div className="bg-gradient-to-br from-blue-50 to-amber-50 rounded-2xl p-6">
                <h4 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-3">
                  Expected Outcomes
                </h4>
                <ul className="space-y-2">
                  {expandedArea.outcomes.map((outcome) => (
                    <li
                      key={outcome}
                      className="flex items-center gap-2 text-slate-700 font-medium"
                    >
                      <span className="w-5 h-5 rounded-full bg-brand-amber/20 flex items-center justify-center">
                        <span className="w-2 h-2 rounded-full bg-brand-amber" />
                      </span>
                      {outcome}
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA */}
              <div className="pt-4">
                <a
                  href="#contact"
                  className="btn-primary w-full justify-center"
                  onClick={(e) => {
                    e.preventDefault()
                    setExpandedCard(null)
                    setTimeout(() => {
                      document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })
                    }, 300)
                  }}
                >
                  Discuss Your {expandedArea.title.split(' ')[0]} Needs
                  <ChevronRight size={18} aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
